import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
  title?: string;
  lockOrientation?: boolean;
  /** Reject 8/12/13-digit reads whose UPC/EAN check digit fails. Off for
   *  non-retail codes such as deposit-bag serials, which have no check digit. */
  validateRetailChecksum?: boolean;
  /** Label and placeholder for the type-it-in fallback. */
  manualLabel?: string;
  manualPlaceholder?: string;
}

/**
 * Validates Modulo-10 check digits for standard numeric barcodes (UPC-A, EAN-13, EAN-8).
 * Discards partial/glitched frame misreads before they can trigger an erroneous lookup.
 */
export function isValidUpcEanChecksum(code: string): boolean {
  if (!code) return false;
  const digits = code.trim().replace(/\D/g, "");
  
  // Non-numeric formats or custom Alpha-numeric SKUs (Code 128 / Code 39 / QR) pass through
  if (digits.length !== 12 && digits.length !== 13 && digits.length !== 8) {
    return code.trim().length >= 3;
  }

  const nums = digits.split("").map(Number);
  const checkDigit = nums[nums.length - 1];
  let sum = 0;

  if (digits.length === 12) {
    // UPC-A: 3x on 0-indexed even positions + 1x on 0-indexed odd positions
    for (let i = 0; i < 11; i++) {
      sum += nums[i] * (i % 2 === 0 ? 3 : 1);
    }
  } else if (digits.length === 13) {
    // EAN-13: 1x on 0-indexed even positions + 3x on 0-indexed odd positions
    for (let i = 0; i < 12; i++) {
      sum += nums[i] * (i % 2 === 1 ? 3 : 1);
    }
  } else if (digits.length === 8) {
    // EAN-8: 3x on 0-indexed even positions + 1x on 0-indexed odd positions
    for (let i = 0; i < 7; i++) {
      sum += nums[i] * (i % 2 === 0 ? 3 : 1);
    }
  }

  const calculatedCheck = (10 - (sum % 10)) % 10;
  return calculatedCheck === checkDigit;
}

export default function BarcodeScannerModal({
  isOpen,
  onClose,
  onScan,
  title = "Scan Bottle Barcode",
  lockOrientation = true,
  validateRetailChecksum = true,
  manualLabel = "Enter UPC / SKU",
  manualPlaceholder = "Enter barcode UPC/SKU",
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const generationRef = useRef(0);
  const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openRef = useRef(isOpen);
  const onScanRef = useRef(onScan);
  openRef.current = isOpen;
  onScanRef.current = onScan;
  const [manualCode, setManualCode] = useState("");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedCode, setLockedCode] = useState<string | null>(null);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const softwareControlsRef = useRef<IScannerControls | null>(null);

  // Multi-frame consensus tracking refs (eliminates single-frame motion blur noise)
  const candidateRef = useRef<string | null>(null);
  const matchCountRef = useRef<number>(0);
  const lastCandidateTimeRef = useRef<number>(0);
  const lockTriggeredRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      return;
    }

    let active = true;
    // Existing scanner callers retain portrait locking; Station can preserve tablet orientation.
    try {
      if (lockOrientation && screen.orientation && typeof (screen.orientation as any).lock === "function") {
        (screen.orientation as any).lock("portrait").then(() => {
          if (!active) screen.orientation.unlock();
        }).catch(() => {
          // Normal fallback for browsers/modes where orientation lock is restricted
        });
      }
    } catch {}

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheetRef.current?.focus();
    setManualCode("");
    void startCamera();

    return () => {
      active = false;
      stopCamera();
      if (previousFocus?.isConnected) previousFocus.focus();
      try {
        if (lockOrientation && screen.orientation && typeof screen.orientation.unlock === "function") {
          screen.orientation.unlock();
        }
      } catch {}
    };
  }, [isOpen, lockOrientation]);

  const playBeep = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime); // High pleasant A5
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.onended = () => { void ctx.close(); };
      osc.stop(ctx.currentTime + 0.08);
    } catch {
      // Audio autoplay policy catch
    }
  };

  const startCamera = async () => {
    stopCamera();
    const generation = generationRef.current;
    const isCurrent = () => openRef.current && generation === generationRef.current;
    setCameraError(null);
    setIsScanning(true);
    setIsLocked(false);
    setLockedCode(null);
    setTorchOn(false);
    lockTriggeredRef.current = false;
    candidateRef.current = null;
    matchCountRef.current = 0;
    lastCandidateTimeRef.current = 0;

    try {
      let detector: any = null;
      if ((window as any).BarcodeDetector) {
        try {
          const Detector = (window as any).BarcodeDetector;
          const requestedFormats = ["upc_a", "upc_e", "ean_13", "ean_8", "code_128", "code_39", "qr_code", "data_matrix"];
          const availableFormats: string[] = typeof Detector.getSupportedFormats === "function"
            ? await Detector.getSupportedFormats()
            : requestedFormats;
          if (!isCurrent()) return;
          const formats = requestedFormats.filter((format) => availableFormats.includes(format));
          if (formats.length) detector = new Detector({ formats });
        } catch {
          // Safari and some tablet browsers need the software decoder.
        }
      }
      const softwareReader = detector ? null : await loadSoftwareReader();
      if (!isCurrent()) return;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera API is not supported in this browser environment.");
      }

      // Request high-definition 1080p stream for crisp barcode edge definition
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          aspectRatio: { ideal: 1.7777777778 },
        },
        audio: false,
      });

      if (!isCurrent()) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      // Enable continuous hardware autofocus and exposure on supported mobile devices
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const capabilities: any = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
        if (capabilities.torch) {
          setTorchAvailable(true);
        }

        const advanced: any[] = [];
        if (capabilities.focusMode && Array.isArray(capabilities.focusMode) && capabilities.focusMode.includes("continuous")) {
          advanced.push({ focusMode: "continuous" });
        }
        if (capabilities.exposureMode && Array.isArray(capabilities.exposureMode) && capabilities.exposureMode.includes("continuous")) {
          advanced.push({ exposureMode: "continuous" });
        }
        if (capabilities.whiteBalanceMode && Array.isArray(capabilities.whiteBalanceMode) && capabilities.whiteBalanceMode.includes("continuous")) {
          advanced.push({ whiteBalanceMode: "continuous" });
        }
        if (advanced.length > 0) {
          try {
            await (videoTrack as any).applyConstraints({ advanced });
          } catch {}
        }
      }

      if (!isCurrent()) return;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if (!isCurrent()) return;
        if (detector) startDetection(detector, generation);
        else await startSoftwareDetection(generation, softwareReader!);
      }
    } catch (err: any) {
      if (!isCurrent()) return;
      console.warn("Camera init failed:", err);
      stopCamera();
      setCameraError(
        err?.message || "Camera access denied. You can still enter or paste the barcode manually below."
      );
      setIsScanning(false);
    }
  };

  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    const generation = generationRef.current;
    try {
      await (track as any).applyConstraints({
        advanced: [{ torch: next }],
      });
      if (openRef.current && generation === generationRef.current) setTorchOn(next);
    } catch (err) {
      console.warn("Could not toggle torch:", err);
    }
  };

  const stopCamera = () => {
    generationRef.current += 1;
    const softwareControls = softwareControlsRef.current;
    softwareControlsRef.current = null;
    softwareControls?.stop();
    if (scanTimeoutRef.current !== null) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsScanning(false);
    setIsLocked(false);
    setLockedCode(null);
    setTorchOn(false);
    setTorchAvailable(false);
    candidateRef.current = null;
    matchCountRef.current = 0;
    lockTriggeredRef.current = false;
  };

  const acceptDetection = (raw: string, generation: number, isQr = false) => {
    const isCurrent = () => openRef.current && generation === generationRef.current;
    const code = raw.trim();
    if (!isCurrent() || lockTriggeredRef.current) return;
    if (validateRetailChecksum ? !isValidUpcEanChecksum(code) : code.length < 3) return;
    const now = performance.now();
    const sameCandidate = candidateRef.current === code && now - lastCandidateTimeRef.current < 350;
    matchCountRef.current = isQr ? 2 : sameCandidate ? matchCountRef.current + 1 : 1;
    candidateRef.current = code;
    lastCandidateTimeRef.current = now;
    if (matchCountRef.current < 2) return;
    lockTriggeredRef.current = true;
    setIsLocked(true);
    setLockedCode(code);
    playBeep();
    navigator.vibrate?.([50, 40, 50]);
    scanTimeoutRef.current = setTimeout(() => {
      if (!isCurrent()) return;
      stopCamera();
      onScanRef.current(code);
    }, 120);
  };

  const loadSoftwareReader = async () => {
    try {
      const { BrowserMultiFormatReader: Reader } = await import("@zxing/browser");
      return new Reader(undefined, { delayBetweenScanAttempts: 100, delayBetweenScanSuccess: 100 });
    } catch {
      throw new Error("Barcode scanning could not load. Enter the UPC below.");
    }
  };

  const startSoftwareDetection = async (generation: number, readyReader?: BrowserMultiFormatReader) => {
    const isCurrent = () => openRef.current && generation === generationRef.current;
    try {
      const reader = readyReader ?? await loadSoftwareReader();
      if (!isCurrent()) return;
      const stream = streamRef.current;
      const video = videoRef.current;
      if (!stream || !video) return;
      const controls = await reader.decodeFromStream(stream, video, (result, error, callbackControls) => {
        if (!isCurrent()) {
          callbackControls.stop();
          return;
        }
        if (result) acceptDetection(result.getText(), generation);
        // Ordinary frames without a readable barcode are expected; decoder failures are not.
        else if (error && !["NotFoundException", "ChecksumException", "FormatException"].includes(
          typeof error.getKind === "function" ? error.getKind() : error.name,
        )) {
          callbackControls.stop();
          stopCamera();
          setCameraError("Barcode scanning is unavailable. Enter the UPC below.");
        }
      });
      if (!isCurrent()) {
        controls.stop();
        return;
      }
      softwareControlsRef.current = controls;
    } catch {
      if (!isCurrent()) return;
      stopCamera();
      setCameraError("Barcode scanning is unavailable. Enter the UPC below.");
    }
  };

  const startDetection = (barcodeDetector: any, generation: number) => {
    const isCurrent = () => openRef.current && generation === generationRef.current;
    const detectFrame = async () => {
      if (!isCurrent() || lockTriggeredRef.current) return;
      if (videoRef.current && videoRef.current.readyState >= 2) {
        try {
          const barcodes = await barcodeDetector.detect(videoRef.current);
          if (!isCurrent()) return;
          if (barcodes?.[0]?.rawValue) {
            acceptDetection(barcodes[0].rawValue, generation, ["qr_code", "data_matrix"].includes(barcodes[0].format));
          }
        } catch {
          if (isCurrent()) await startSoftwareDetection(generation);
          return;
        }
      }
      if (isCurrent() && !lockTriggeredRef.current) animFrameRef.current = requestAnimationFrame(detectFrame);
    };
    animFrameRef.current = requestAnimationFrame(detectFrame);
  };

  const handleSuccessfulScan = (code: string) => {
    if (!openRef.current) return;
    playBeep();
    if (navigator.vibrate) {
      navigator.vibrate([40, 30, 40]);
    }
    stopCamera();
    onScanRef.current(code);
  };

  const closeScanner = () => {
    stopCamera();
    onClose();
  };

  const handleManualSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = manualCode.trim();
    if (!clean) return;
    handleSuccessfulScan(clean);
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-sheet-backdrop" style={styles.backdrop} onClick={(event) => { event.stopPropagation(); closeScanner(); }}>
      <div className="mobile-sheet-content" style={styles.sheet} onClick={(e) => e.stopPropagation()}
        ref={sheetRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            closeScanner();
          }
          if (event.key === "Tab") {
            event.stopPropagation();
            const controls = Array.from(sheetRef.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled), input:not(:disabled), [tabindex="0"]',
            ) ?? []);
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && (document.activeElement === first || document.activeElement === sheetRef.current)) {
              event.preventDefault(); last?.focus();
            } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === sheetRef.current)) {
              event.preventDefault(); first?.focus();
            }
          }
        }}>
        <div className="mobile-sheet-handle" />

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{title}</h2>
            <p
              style={{
                ...styles.subtitle,
                color: isLocked ? "#86efac" : isScanning ? "var(--text)" : "var(--muted)",
                fontWeight: isLocked ? 700 : 400,
              }}
            >
              {isLocked
                ? `✓ Barcode Verified & Locked: ${lockedCode || ""}`
                : isScanning
                ? "🟢 Scanning active — align barcode inside frame"
                : cameraError ? "Enter the barcode manually below" : "Align bottle barcode inside the gold frame"}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {torchAvailable && (
              <button
                type="button"
                onClick={toggleTorch}
                style={{
                  ...styles.torchBtn,
                  background: torchOn ? "rgba(249,175,54,0.25)" : "transparent",
                  borderColor: torchOn ? "var(--gold, #F9AF36)" : "var(--border, #27272a)",
                  color: torchOn ? "var(--gold, #F9AF36)" : "var(--muted, #a1a1aa)",
                }}
                title={torchOn ? "Turn flashlight off" : "Turn flashlight on for dark bar lighting"}
              >
                ⚡ {torchOn ? "Light ON" : "Torch"}
              </button>
            )}
            <button type="button" aria-label="Close scanner" onClick={closeScanner} style={styles.closeBtn}>
              ✕
            </button>
          </div>
        </div>

        {/* Camera Viewfinder */}
        <div style={styles.viewfinderContainer}>
          {cameraError ? (
            <div style={styles.cameraErrorBox}>
              <span style={{ fontSize: 28, marginBottom: 8 }}>📷</span>
              <p style={{ margin: 0, fontSize: 13, color: "var(--text)" }}>Scanning unavailable</p>
              <p style={{ margin: "4px 0 12px", fontSize: 11, color: "var(--muted)" }}>{cameraError}</p>
              
            </div>
          ) : (
            <div style={styles.videoWrapper}>
              <video ref={videoRef} playsInline muted style={styles.video} />
              
              {/* Scan Reticle (Turns Emerald Green when Verified & Locked) */}
              <div style={styles.reticle}>
                <div
                  style={{
                    ...styles.reticleCornerTL,
                    borderColor: isLocked ? "#27ae60" : "var(--gold, #F9AF36)",
                    boxShadow: isLocked ? "0 0 12px #27ae60" : "none",
                  }}
                />
                <div
                  style={{
                    ...styles.reticleCornerTR,
                    borderColor: isLocked ? "#27ae60" : "var(--gold, #F9AF36)",
                    boxShadow: isLocked ? "0 0 12px #27ae60" : "none",
                  }}
                />
                <div
                  style={{
                    ...styles.reticleCornerBL,
                    borderColor: isLocked ? "#27ae60" : "var(--gold, #F9AF36)",
                    boxShadow: isLocked ? "0 0 12px #27ae60" : "none",
                  }}
                />
                <div
                  style={{
                    ...styles.reticleCornerBR,
                    borderColor: isLocked ? "#27ae60" : "var(--gold, #F9AF36)",
                    boxShadow: isLocked ? "0 0 12px #27ae60" : "none",
                  }}
                />
                <div
                  style={{
                    ...styles.laser,
                    background: isLocked ? "rgba(39, 174, 96, 0.95)" : "rgba(249, 175, 54, 0.9)",
                    boxShadow: isLocked ? "0 0 12px #27ae60" : "0 0 8px #F9AF36",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleManualSubmit} style={styles.manualForm}>
          <label style={styles.fieldLabel}>
            {manualLabel}
            <input type="text" autoComplete="off" placeholder={manualPlaceholder}
              value={manualCode} onChange={(e) => setManualCode(e.target.value)}
              style={{ ...styles.input, display: "block", width: "100%", boxSizing: "border-box", marginTop: 6 }} />
          </label>
          <button type="submit" style={{ ...styles.submitBtn, minHeight: 44 }} disabled={!manualCode.trim()}>
            Lookup
          </button>
        </form>
        <div style={styles.scannerFooterTip}>
          <span>💡 Scans barcodes at any angle — no need to turn your phone sideways</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.82)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 10050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  sheet: {
    background: "var(--surface, #18181b)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 16,
    width: "100%",
    maxWidth: 460,
    padding: "20px 20px 24px 20px",
    maxHeight: "calc(100dvh - 32px)",
    overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  title: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    letterSpacing: 0.3,
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: 11,
    color: "var(--muted, #a1a1aa)",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #a1a1aa)",
    fontSize: 18,
    cursor: "pointer",
    padding: 4,
  },
  viewfinderContainer: {
    position: "relative",
    width: "100%",
    height: 220,
    background: "#000",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid rgba(249, 175, 54, 0.25)",
    marginBottom: 16,
  },
  videoWrapper: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  reticle: {
    position: "absolute",
    width: "72%",
    height: "60%",
    pointerEvents: "none",
  },
  reticleCornerTL: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 20,
    height: 20,
    borderTop: "3px solid var(--gold, #F9AF36)",
    borderLeft: "3px solid var(--gold, #F9AF36)",
    borderTopLeftRadius: 6,
  },
  reticleCornerTR: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 20,
    height: 20,
    borderTop: "3px solid var(--gold, #F9AF36)",
    borderRight: "3px solid var(--gold, #F9AF36)",
    borderTopRightRadius: 6,
  },
  reticleCornerBL: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 20,
    height: 20,
    borderBottom: "3px solid var(--gold, #F9AF36)",
    borderLeft: "3px solid var(--gold, #F9AF36)",
    borderBottomLeftRadius: 6,
  },
  reticleCornerBR: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 20,
    height: 20,
    borderBottom: "3px solid var(--gold, #F9AF36)",
    borderRight: "3px solid var(--gold, #F9AF36)",
    borderBottomRightRadius: 6,
  },
  laser: {
    position: "absolute",
    top: "50%",
    left: "5%",
    right: "5%",
    height: 2,
    background: "rgba(249, 175, 54, 0.9)",
    boxShadow: "0 0 8px #F9AF36",
  },
  cameraErrorBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: 20,
    textAlign: "center",
  },
  manualForm: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--muted, #a1a1aa)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  inputRow: {
    display: "flex",
    gap: 8,
  },
  input: {
    flex: 1,
    background: "var(--bg, #09090b)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 8,
    padding: "10px 14px",
    color: "var(--text, #f4f4f5)",
    fontSize: 13,
    outline: "none",
  },
  submitBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#000",
    border: "none",
    borderRadius: 8,
    padding: "0 18px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  torchBtn: {
    border: "1px solid var(--border, #27272a)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  scannerFooterTip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 11,
    color: "var(--muted, #a1a1aa)",
    lineHeight: 1.3,
    marginTop: 4,
  },
};
